import { useQuery } from '@tanstack/react-query'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { getCustomerWallet, getCustomerWallets } from 'services/CustomerService'
import {
  LedgerWalletCapabilities,
  type CustomerWallet,
  type CustomerWalletsRequest,
  type CustomerWalletsV2Response,
  type CustomerWalletV2,
  type CustomerWalletV2Individual,
  type Ledger,
} from 'types/customers'
import type { WithPaginationPaging } from 'types/pagination'
import { API_PAGINATION_LIMIT, API_PAGINATION_OFFSET } from 'utils/pagination'

const mapV2LedgerToV1Format = (ledger: Ledger): Ledger => ({
  ...ledger,
  code: ledger?.code ?? ledger?.bank?.identificationCode,
  paymentReference: ledger?.paymentReferenceRequiredPrefix ?? ledger?.paymentReference,
})

const transformWalletV2ToCustomerWallet = (wallet: CustomerWalletV2): CustomerWallet => ({
  id: wallet?.id,
  accountReference: '',
  customerReference: wallet?.customerId ?? wallet?.customer?.id ?? '',
  name: wallet?.name,
  status: wallet?.status,
  balance: {
    value: wallet?.balance?.amount ?? 0,
    currencyCode: wallet?.balance?.currency ?? '',
  },
  ledgers: (wallet?.ledgers ?? []).map(mapV2LedgerToV1Format),
  paymentInstruments: (wallet?.paymentInstruments ?? []).map(mapV2LedgerToV1Format),
  capabilities: wallet?.capabilities?.safeguarded ? [LedgerWalletCapabilities.SAFEGUARDED] : [],
  custodyModel: wallet?.custodyModel,
})

const transformIndividualWalletV2ToCustomerWallet = (
  wallet: CustomerWalletV2Individual
): CustomerWallet => ({
  id: wallet?.id,
  accountReference: '',
  customerReference: wallet?.customer?.id ?? '',
  name: wallet?.name,
  status: wallet?.status,
  balance: {
    value: wallet?.balance?.amount ?? 0,
    currencyCode: wallet?.balance?.currency ?? '',
  },
  ledgers: (wallet?.ledgers ?? [])?.map(mapV2LedgerToV1Format),
  paymentInstruments: (wallet?.paymentInstruments ?? [])?.map(mapV2LedgerToV1Format),
  capabilities: wallet?.capabilities?.safeguarded ? [LedgerWalletCapabilities.SAFEGUARDED] : [],
  custodyModel: wallet?.custodyModel,
})

const transformWalletsV2ResponseWithPagination = (
  res: CustomerWalletsV2Response
): WithPaginationPaging<CustomerWallet[]> => {
  const pageNumber = res.pageable?.pageNumber ?? 0
  const pageSize = res.pageable?.pageSize ?? 0
  const contentLength = res.content?.length ?? 0
  const estimatedTotal = res.hasNext
    ? (pageNumber + 2) * pageSize
    : pageNumber * pageSize + contentLength

  return {
    content: res.content?.map(transformWalletV2ToCustomerWallet) ?? [],
    pagination: {
      max: pageSize,
      offset: pageNumber,
      page: pageNumber,
      total: estimatedTotal,
    },
  }
}

export const useGetCustomersWalletsQuery = ({
  customerReference,
  currencyCodeSearch,
  currencyCodes,
  currencyType,
  searchTerm,
  size = API_PAGINATION_LIMIT,
  page = API_PAGINATION_OFFSET,
  sort = '',
  statuses,
}: CustomerWalletsRequest) => {
  return useQuery({
    enabled: !!customerReference,
    queryKey: reactQueryKeys.customerWallets({
      customerReference,
      ...(size && { size }),
      ...(page && { page }),
      ...(sort && { sort }),
      ...(currencyCodeSearch && { currencyCodeSearch }),
      ...(currencyCodes?.length && { currencyCodes }),
      ...(currencyType && { currencyType }),
      ...(searchTerm && { searchTerm }),
      ...(statuses?.length && { statuses }),
    }),
    queryFn: () =>
      getCustomerWallets({
        customerReference,
        size,
        page,
        sort,
        currencyCodeSearch,
        currencyCodes,
        currencyType,
        searchTerm,
        statuses,
      }),
    select: transformWalletsV2ResponseWithPagination,
  })
}

export const useGetCustomersWalletQuery = (walletId: string, enabled = false) => {
  return useQuery({
    enabled,
    queryKey: reactQueryKeys.customerWallet(walletId),
    queryFn: () => getCustomerWallet(walletId),
    select: transformIndividualWalletV2ToCustomerWallet,
  })
}
